import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../common';
import { aiSystemApi } from '../../services/api';
import { AISystemFormAdvanced } from './AISystemFormAdvanced';
import PageBackButton from '../../../components/PageBackButton';

const AISystemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch AI system details with refreshKey dependency
  const { data: system, isLoading, error, refetch } = useQuery({
    queryKey: ['ai-system', id, refreshKey],
    queryFn: () => aiSystemApi.get(Number(id)),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always fetch fresh data
  });

  const handleClose = () => {
    // Navigate back to systems list
    navigate('/ai/systems');
  };

  const systemForView = useMemo(() => {
    if (!system) {
      return system;
    }

    if (searchParams.get('mode') !== 'ai') {
      return system;
    }

    return {
      ...system,
      config: {
        ...(system.config || {}),
        initial_builder_mode: 'ai',
      },
    };
  }, [searchParams, system]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !system) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="AI System Not Found"
          backButton={<PageBackButton label="Back to Systems" to="/ai/systems" preferHistory={false} hideLabelOnMobile />}
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.message || 'System not found'}
        </Alert>
      </Box>
    );
  }

  const handleSystemUpdate = async () => {
    console.log('ðŸ”„ AISystemDetail: System updated by AI, triggering refetch...');
    // Invalidate and refetch the system query
    await queryClient.invalidateQueries({ queryKey: ['ai-system', id] });
    const result = await refetch();
    console.log('âœ… AISystemDetail: System data refetched', result.data);
    
    // Force form remount by toggling refreshKey
    setRefreshKey(prev => prev + 1);
  };

  return (
    <AISystemFormAdvanced
      key={`system-form-${system?.id}-${searchParams.get('mode') || 'manual'}-${refreshKey}`}
      open={true}
      onClose={handleClose}
      system={systemForView}
      onSystemUpdate={handleSystemUpdate}
      asPage={true}
    />
  );
};

export default AISystemDetail;
